/* eslint-disable max-lines */
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { BasicButton, Icon, IconSize } from "@gaming/xds-react-js";
import { ApplicationLaunch, StatusCircleCheckmark } from "@gaming/xds-react-js-icons";

import { GameConceptApplication } from "../../helpers/gameConceptHelpers";
import { useProgramColorPicker } from "../../helpers/useProgramColorPicker";
import { useStrings } from "../../localization/localizationHelpers";
import { CrmActions } from "../../redux/actions/crmApiActions";
import {
  getCurrentError,
  getCurrentProgram,
  getIsCreatingXpaProgramTracking,
  getIsUpdatingXpaProgramTracking,
} from "../../redux/selectors";
import { treatDateAsUtc } from "../../server/crm/api/mappers/utils/dateUtils";
import { HubProgramDisplayStatusCode } from "../../server/crm/dataService/models/staticOptions";
import { XpaModal } from "../XpaModal";
import * as styles from "./GameConceptsList.module.css";

export const XPA_INITIATIVE_ID = "6107bb87-f473-f011-bec1-7c1e52154e76";

export interface XPAInitiativesProgramRowProps {
  gameConcept: GameConceptApplication;
}

export const XPAInitiativesProgramRow = ({ gameConcept }: XPAInitiativesProgramRowProps) => {
  const [
    XPAIncentiveEligible,
    XPAIncentiveEnrolled,
    XPAIncentiveNotEligible,
    XPAIncentiveUnderReview,
    view,
    untitledGame,
    XPAEnrollmentSaveError,
  ] = useStrings(
    "XPAIncentiveEligible",
    "XPAIncentiveEnrolled",
    "XPAIncentiveNotEligible",
    "XPAIncentiveUnderReview",
    "view",
    "untitledGame",
    "XPAEnrollmentSaveError"
  );

  const dispatch = useDispatch();
  const currentProgram = useSelector(getCurrentProgram);
  const isCreatingXpaProgramTracking = useSelector(getIsCreatingXpaProgramTracking);
  const isUpdatingXpaProgramTracking = useSelector(getIsUpdatingXpaProgramTracking);
  const currentError = useSelector(getCurrentError);
  const colorStyle = useProgramColorPicker();

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalErrorMessage, setModalErrorMessage] = useState<string>("");

  // Track CRM operation states for loading spinner
  const isCrmOperationInProgress = isCreatingXpaProgramTracking || isUpdatingXpaProgramTracking;
  const wasUpdatingRef = useRef(false);
  const actionTypeRef = useRef<"save-opt-in" | "save-exit" | null>(null);

  const initiatives = gameConcept.gameConceptInfo.state.initiatives;
  const programTracking = gameConcept.gameConceptInfo.state.programTracking;
  const hasInitiatives = initiatives && initiatives.length > 0; // In te future => AND if hub only AND creator classification foes not equal core.
  const hasProgramTrackingXPA = programTracking && Object.keys(programTracking).length > 0;
  const gameTitle = gameConcept.gameConceptInfo.details.gameTitle;

  const hubProgramDisplayStatus = hasProgramTrackingXPA
    ? programTracking.gamingHubprogramdisplaystatus
    : null;

  // Close modal after Save & Opt-In or Save & Exit UPDATE operations complete
  useEffect(() => {
    // If we were updating and now we're not, and it was a save action, close the modal
    if (wasUpdatingRef.current && !isUpdatingXpaProgramTracking && actionTypeRef.current) {
      const timer = setTimeout(() => {
        setIsModalOpen(false);
        actionTypeRef.current = null; // Reset action type
        setModalErrorMessage(""); // Clear any error messages
      }, 500); // Small delay to show completion

      return () => clearTimeout(timer);
    }

    // Update the reference
    wasUpdatingRef.current = isUpdatingXpaProgramTracking;

    // Explicit return for cases where the cleanup function is not needed
    return undefined;
  }, [isUpdatingXpaProgramTracking]);

  // Handle Redux errors for XPA Program Tracking operations
  useEffect(() => {
    if (
      currentError &&
      isModalOpen &&
      (isCreatingXpaProgramTracking || isUpdatingXpaProgramTracking)
    ) {
      setModalErrorMessage(XPAEnrollmentSaveError);
    }
  }, [
    currentError,
    isModalOpen,
    isCreatingXpaProgramTracking,
    isUpdatingXpaProgramTracking,
    XPAEnrollmentSaveError,
  ]);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setModalErrorMessage("");
  };

  const handleModalSaveAndOptIn = (selectedDate: Date | null) => {
    if (!selectedDate) {
      return;
    }
    const programTracking = gameConcept.gameConceptInfo.state.programTracking;

    if (programTracking?.gamingProgramid) {
      dispatch({
        type: CrmActions.UPDATE_XPA_PROGRAM_TRACKING_START,
        program: currentProgram,
        gameConceptId: gameConcept.id,
        programId: programTracking.gamingProgramid,
        updateData: {
          selectedDate: selectedDate,
          setSubmittedDate: true,
        },
      });
    } else {
      const initiative = initiatives && initiatives.length > 0 ? initiatives[0] : null;
      if (initiative) {
        dispatch({
          type: CrmActions.CREATE_XPA_PROGRAM_TRACKING_START,
          program: currentProgram,
          createData: {
            initiativeId: XPA_INITIATIVE_ID,
            gameConceptId: gameConcept.id,
            selectedDate: selectedDate,
            setSubmittedDate: true,
          },
        });
      }
    }
    // Set action type and let useEffect close modal after UPDATE operation completes
    actionTypeRef.current = "save-opt-in";
  };

  const handleSaveAndExit = (selectedDate: Date | null) => {
    const programTracking = gameConcept.gameConceptInfo.state.programTracking;
    // Update in case that the program exists
    if (programTracking?.gamingProgramid) {
      dispatch({
        type: CrmActions.UPDATE_XPA_PROGRAM_TRACKING_START,
        program: currentProgram,
        gameConceptId: gameConcept.id,
        programId: programTracking.gamingProgramid,
        updateData: {
          selectedDate: selectedDate ? treatDateAsUtc(selectedDate) : null,
          isSaveAndExit: true,
        },
      });
    } else {
      const initiative = initiatives && initiatives.length > 0 ? initiatives[0] : null;
      if (initiative) {
        dispatch({
          type: CrmActions.CREATE_XPA_PROGRAM_TRACKING_START,
          program: currentProgram,
          createData: {
            initiativeId: XPA_INITIATIVE_ID, // XPA Initiative ID
            gameConceptId: gameConcept.id,
            selectedDate: selectedDate ? treatDateAsUtc(selectedDate) : null,
            setSubmittedDate: false, // Do NOT set submitted date for Save & Exit
            isSaveAndExit: true,
          },
        });
      }
    }
    // Set action type and let useEffect close modal after UPDATE operation completes
    actionTypeRef.current = "save-exit";
  };

  // Initialize the showEligibleModal function
  const showXPAEligibleModal = () => {
    // Update Hub Opened timestamp when opening modal
    const programTracking = gameConcept.gameConceptInfo.state.programTracking;
    if (programTracking?.gamingProgramid) {
      dispatch({
        type: CrmActions.UPDATE_XPA_PROGRAM_TRACKING_START,
        program: currentProgram,
        gameConceptId: gameConcept.id,
        programId: programTracking.gamingProgramid,
        updateData: {
          updateHubOpened: true, // Update gaming_hubopened to current timestamp
        },
      });
    }

    setIsModalOpen(true);
  };

  const handleEligibleButtonClick = () => {
    const programTracking = gameConcept.gameConceptInfo.state.programTracking;

    // Check if a CRM record already exists (has gamingProgramid)
    if (programTracking?.gamingProgramid) {
      // CRM record exists - show modal directly
      showXPAEligibleModal();
    } else if (hasInitiatives) {
      // Show modal with spinner immediately
      showXPAEligibleModal();

      // Dispatch creation of empty draft (loading state managed by saga)
      dispatch({
        type: CrmActions.CREATE_XPA_PROGRAM_TRACKING_START,
        program: currentProgram,
        createData: {
          initiativeId: XPA_INITIATIVE_ID,
          gameConceptId: gameConcept.id,
          createEmptyDraft: true,
        },
      });
    }
  };

  const returnXpaIncentiveEligibleButton = () => (
    <div className={styles.gameConceptEligible}>
      <span className={styles.xpaEligibleText}>{XPAIncentiveEligible}</span>
      <BasicButton
        as={"button"}
        onClick={handleEligibleButtonClick}
        onKeyDown={e => e.key === "Enter" && handleEligibleButtonClick()}
        tabIndex={0}
        role="link"
        textDecorationUnderline={true}
        className={styles.xpaApplyButton}
      >
        <span className={styles.xpaApplyText}>Apply</span>
        <Icon aria-hidden={true} icon={ApplicationLaunch} iconSize={IconSize.Custom} />
      </BasicButton>
    </div>
  );

  switch (hubProgramDisplayStatus) {
    case HubProgramDisplayStatusCode.Draft:
      return hasInitiatives ? (
        <>
          {returnXpaIncentiveEligibleButton()}
          <XpaModal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            saveAndOptIn={handleModalSaveAndOptIn}
            onSaveAndExit={handleSaveAndExit}
            gameTitle={gameTitle !== "" ? gameTitle : untitledGame}
            gameConcept={gameConcept}
            isLoading={isCrmOperationInProgress}
            errorMessage={modalErrorMessage}
          />
        </>
      ) : (
        <div className={styles.gameConceptEligible}></div>
      );
    case HubProgramDisplayStatusCode.Enrolled:
      return (
        <>
          <div className={styles.enrolledContainer}>
            <div className={styles.successIconContainer}>
              <Icon
                aria-hidden={true}
                icon={StatusCircleCheckmark}
                iconSize={IconSize.Custom}
                className={styles.successIcon}
              />
            </div>
            <span className={styles.xpaEnrolledText}>{XPAIncentiveEnrolled}</span>
            <BasicButton
              as={"button"}
              onClick={showXPAEligibleModal}
              onKeyDown={e => e.key === "Enter" && showXPAEligibleModal()}
              tabIndex={0}
              role="link"
              textDecorationUnderline={true}
              className={`${colorStyle} ${styles.xpaViewButton}`}
            >
              <span className={colorStyle}>{view}</span>
            </BasicButton>
          </div>
          <XpaModal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            saveAndOptIn={handleModalSaveAndOptIn}
            onSaveAndExit={handleSaveAndExit}
            gameTitle={gameTitle !== "" ? gameTitle : untitledGame}
            gameConcept={gameConcept}
            isLoading={isCrmOperationInProgress}
            errorMessage={modalErrorMessage}
          />
        </>
      );
    case HubProgramDisplayStatusCode.NotEligible:
      return hasInitiatives ? (
        <div className={styles.gameConceptEligible}>
          <span className={styles.xpaNotEligible}>{XPAIncentiveNotEligible}</span>
        </div>
      ) : (
        <div className={styles.gameConceptEligible}></div>
      );
    case HubProgramDisplayStatusCode.UnderReview:
      return (
        <>
          <div className={styles.enrolledContainer}>
            <span className={styles.xpaUnderReview}>{XPAIncentiveUnderReview}</span>
            <BasicButton
              as={"button"}
              onClick={() => {
                // Important: If a program is under review, we need to ensure we have this flag
                // in case the user edits and saves - this prevents the status from getting accidentally changed
                const programTracking = gameConcept.gameConceptInfo.state.programTracking;
                if (
                  programTracking?.gamingProgramid &&
                  programTracking.gamingHubprogramdisplaystatus ===
                    HubProgramDisplayStatusCode.UnderReview
                ) {
                  dispatch({
                    type: CrmActions.UPDATE_XPA_PROGRAM_TRACKING_START,
                    program: currentProgram,
                    gameConceptId: gameConcept.id,
                    programId: programTracking.gamingProgramid,
                    updateData: {
                      markAsUnderReview: true,
                    },
                  });
                }
                showXPAEligibleModal();
              }}
              onKeyDown={e => e.key === "Enter" && showXPAEligibleModal()}
              tabIndex={0}
              role="link"
              textDecorationUnderline={true}
              className={`${colorStyle} ${styles.xpaViewButton}`}
            >
              <span className={colorStyle}>{view}</span>
            </BasicButton>
          </div>
          <XpaModal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            saveAndOptIn={handleModalSaveAndOptIn}
            onSaveAndExit={handleSaveAndExit}
            gameTitle={gameTitle !== "" ? gameTitle : untitledGame}
            gameConcept={gameConcept}
            isLoading={isCrmOperationInProgress}
            errorMessage={modalErrorMessage}
          />
        </>
      );
    case HubProgramDisplayStatusCode.Block:
      return <div className={styles.gameConceptEligible}></div>;
    case null:
    default:
      return hasInitiatives ? (
        <>
          {returnXpaIncentiveEligibleButton()}
          <XpaModal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            saveAndOptIn={handleModalSaveAndOptIn}
            onSaveAndExit={handleSaveAndExit}
            gameTitle={gameTitle !== "" ? gameTitle : untitledGame}
            gameConcept={gameConcept}
            isLoading={isCrmOperationInProgress}
            errorMessage={modalErrorMessage}
          />
        </>
      ) : (
        <div className={styles.gameConceptEligible}></div>
      );
  }
};
